import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RndResizeCallback } from 'react-rnd';
import { getTimeSlots, mapBookingItemsToSlots } from 'utils/booking';
import { generateTimeRange } from 'utils/date';
import moment from 'utils/dayjs';
// constants
import { AVAILABILITY_STATUSES } from '../../constants/fieldData';
import { MINUTES_PER_INCREMENT, TIMESLOT_STEP } from '../../constants/timeslot';
// components
import ScrollArea, { ScrollAreaHandle } from '../scrollArea';
import Slot from './slot';

// the size of time step in widget in px
const timeStepSize = 25;

interface SlotType {
  startTime: string;
  endTime: string;
}

interface Props {
  startTime: string;
  endTime: string;
  bookingItems?: SlotType[];
  availability: object;
  disabled?: boolean;
  loading?: boolean;
  horizontal?: boolean;
  scale?: number;
  value?: SlotType[];
  onChange?: (value: SlotType[]) => void;
}

const emptyList: SlotType[] = [];
const emptyObject = {};
export const noOp = () => {};

const TimeslotWidget: FC<Props> = ({
  startTime = '08:30',
  endTime = '18:30',
  bookingItems = emptyList,
  availability = emptyObject,
  disabled = true,
  loading = false,
  horizontal = false,
  scale = 1,
  value = emptyList,
  onChange: setValue = noOp,
}) => {
  const readOnly = setValue === noOp;

  // get the time step options between start and end times
  const times = useMemo(
    () => generateTimeRange(startTime, endTime, false),
    [startTime, endTime]
  );

  const slots = useMemo(
    () => [
      ...(loading ? [] : getTimeSlots(availability, value)),
      ...mapBookingItemsToSlots(bookingItems),
    ],
    [availability, bookingItems, value, loading]
  );

  const getFirstSlotPosition = useCallback(() => {
    const slot = slots.find(slot => !!slot.available);
    return startTime && endTime && slot
      ? times.indexOf(slot.startTime) * timeStepSize * scale
      : 0;
  }, [startTime, endTime, slots, scale, times]);

  const [scrollState, setScrollState] = useState(() => ({
    [horizontal ? 'x' : 'y']: getFirstSlotPosition(),
  }));

  // PRIVATE: scrolls the widget content area to a give position, 0 (zero) is the top
  // set relative to true if the new position should be relative to current position
  // i.e. add 50 to it's current position rather than set position to 50
  const scrollToPosition = useCallback(
    (position = 0, relative = false) => {
      const axis = horizontal ? 'x' : 'y';
      const axisMax = horizontal ? 'maxX' : 'maxY';
      setScrollState(({ [axis]: prev = 0, [axisMax]: max = 0, ...state }) => ({
        ...state,
        [axisMax]: max,
        [axis]: Math.max(
          0,
          Math.min(max, relative ? prev + position : position)
        ),
      }));
    },
    [horizontal]
  );

  // PRIVATE: decorate time slot with data and positioning
  const getFormattedSlot = useCallback(
    (
      startTime: string,
      endTime: string,
      rest: { available?: string; editable?: boolean }
    ) => {
      const startIndex = times.indexOf(startTime);

      const startTimestamp = moment(startTime, 'HH:mm');
      const endTimestamp = moment(endTime, 'HH:mm');

      const timeDifferenceInHours = moment
        .duration(endTimestamp.diff(startTimestamp))
        .asHours();

      return {
        startTime,
        endTime,
        edge: startIndex * (timeStepSize / 2),
        length: timeDifferenceInHours * timeStepSize * scale,
        ...rest,
      };
    },
    [times, scale]
  );

  // decorate slot objects with scroller positioning ready for rendering below
  const formattedSlots = slots.map(({ startTime, endTime, ...rest }) =>
    getFormattedSlot(startTime, endTime, rest)
  );

  const handleBackwardClick = () => scrollToPosition(timeStepSize * -2, true);
  const handleForwardClick = () => scrollToPosition(timeStepSize * 2, true);

  const scrollRef = useRef<ScrollAreaHandle>(null);

  const handleSlotResize: RndResizeCallback = (
    _event,
    _dir,
    ref,
    _delta,
    { x, y }
  ) => {
    const position = horizontal ? x : y;
    const startOffset = Math.round(
      (position / TIMESLOT_STEP / scale) * MINUTES_PER_INCREMENT
    );
    const length = horizontal ? ref.style.width : ref.style.height;
    const slotStart = moment(startTime, 'HH:mm').add(
      startOffset - (startOffset % MINUTES_PER_INCREMENT),
      'minutes'
    );
    const slotEnd = slotStart
      .clone()
      .add(
        (parseInt(length, 10) / TIMESLOT_STEP / scale) * MINUTES_PER_INCREMENT,
        'minutes'
      );

    setValue([
      {
        startTime: slotStart.isBefore(moment(startTime, 'HH:mm'))
          ? startTime
          : slotStart.format('HH:mm'),
        endTime: slotEnd.isAfter(moment(endTime, 'HH:mm'))
          ? endTime
          : slotEnd.format('HH:mm'),
      },
    ]);

    scrollRef.current?.unlock();
  };

  useEffect(() => {
    if (startTime && endTime && slots.length) {
      scrollToPosition(getFirstSlotPosition());
    }
  }, [
    startTime,
    endTime,
    slots.length,
    scrollToPosition,
    getFirstSlotPosition,
  ]);

  const renderTime = (time: string, index: number) => {
    const [, minutes] = time.split(':');
    const isHalfHour = minutes === '30';
    const endTime = times[index + 2]; // Just to the value that would be the end of the hourly slot

    const style = {
      flexBasis: TIMESLOT_STEP * scale,
    };

    const onClick = () => setValue([{ startTime: time, endTime }]);

    if (isHalfHour) {
      return (
        <button
          type="button"
          key={index}
          onClick={onClick}
          className="timeslot-widget__time-label timeslot-widget__time-label--half-hour"
          style={style}
        />
      );
    }

    return (
      <button
        type="button"
        key={index}
        onClick={onClick}
        className="timeslot-widget__time-label timeslot-widget__time-label--hour"
        style={style}
      >
        <span className="timeslot-widget__time-label-text">{time}</span>
      </button>
    );
  };

  const canScrollBackward = () => {
    const { x = 0, y = 0 } = scrollState;
    return horizontal ? x > 0 : y > 0;
  };

  const canScrollForward = () => {
    const { x = 0, y = 0, maxX = 0, maxY = 0 } = scrollState;
    return horizontal ? x < maxX : y < maxY;
  };

  const className = horizontal
    ? 'timeslot-widget__horizontal'
    : 'timeslot-widget__vertical';

  return (
    <div
      className={`timeslot-widget ${className}`}
      data-testid="timeslotContainer"
    >
      <button
        className="timeslot-widget__button timeslot-widget__button--backward"
        onClick={handleBackwardClick}
        disabled={!canScrollBackward()}
      >
        <i className={horizontal ? 'fas fa-caret-left' : 'fas fa-caret-up'} />
      </button>

      <ScrollArea
        className="timeslot-widget__inner"
        scrollbars={false}
        state={scrollState}
        setState={setScrollState}
        horizontal={horizontal}
        vertical={!horizontal}
        ref={scrollRef}
      >
        <div className="timeslot-widget__content">
          {times.slice(0, times.length - 1).map(renderTime)}

          {loading && (
            <div className="field__loading" data-testid="availabilityLoader" />
          )}

          {!loading &&
            formattedSlots.map(slot => (
              <Slot
                key={`${slot.startTime}-${slot.endTime}`}
                availabilityStatus={
                  AVAILABILITY_STATUSES[
                    slot.available as keyof typeof AVAILABILITY_STATUSES
                  ] || AVAILABILITY_STATUSES.UNAVAILABLE
                }
                length={slot.length * 2}
                edge={slot.edge * 2}
                resizable={!readOnly && slot.editable}
                handleResize={handleSlotResize as () => void}
                {...({
                  onResizeStart: scrollRef.current?.lock,
                  onResizeStop: scrollRef.current?.unlock,
                } as {})}
                horizontal={horizontal}
                scale={scale}
              />
            ))}
        </div>
      </ScrollArea>

      <button
        className="timeslot-widget__button timeslot-widget__button--forward"
        onClick={handleForwardClick}
        disabled={!canScrollForward()}
      >
        <i
          className={horizontal ? 'fas fa-caret-right' : 'fas fa-caret-down'}
        />
      </button>

      {disabled && <div className="timeslot-widget__overlay" />}
    </div>
  );
};

export default TimeslotWidget;
